import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPLAN_ROLES, resolveCapabilities, type ComPlanRole } from '@complan/domain';
import type { RoleAssignmentView } from '@complan/contracts';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EditorDialog, EditorDialogActions } from '@/shared/components/EditorDialog';
import { FindingsAlert } from './FindingsAlert';
import { useMasterDataSet, useRoleAssignments, useUpdateRoleAssignment } from './admin-data';
import { findingsFromError } from './admin-findings';

/**
 * The role-assignment screen (task 23.6, R1.7-R1.10, R2.4, R9.3).
 *
 * Lists every user's assignment aggregate — role set, approver rights, and
 * Platform/Channel read scope — and edits one user's whole assignment through a
 * PUT (a whole-assignment replace, so the audit records the previous and new role
 * set in one event, R1.9). The change takes effect on that user's next request
 * without a sign-out (R1.10). Platform and Channel scope are offered as the active
 * Platform/Channel master data records so a scope is chosen by name, not by id.
 *
 * ## Direct roles only, and why that is said out loud
 *
 * The role set here is `user_role_assignment` — the directly administered grants of
 * R1.9. It is **not** the user's effective role set: R1.2 also derives roles from
 * Identity_Service group membership through `role_group_mapping`, and a
 * group-derived role appears nowhere on this screen. It cannot: group membership
 * reaches the server only inside the affected user's own sign-in token
 * (`request.session.groupObjectIds`), so there is no way to read another user's
 * memberships without a directory round trip this screen does not make.
 *
 * That is why the column reads "Direct roles" and an empty set reads "None assigned
 * directly" rather than "No roles". The previous wording invited a real and costly
 * misreading — that a user shown with no roles held none — when in a group-managed
 * deployment an empty direct set is the *normal* state.
 *
 * ## Approver rights are a per-person grant
 *
 * `approverRights` is stored on `app_user`, independently of the role set
 * (migration 29), so saving an empty role set revokes every direct role and keeps a
 * granted approver right. Before that migration the flag rode on the assignment
 * rows, and submitting an empty role set discarded it silently on a `200 OK` — the
 * exact submission this screen produces for a group-derived user. The flag is
 * therefore editable here for every user, whatever their roles look like.
 */
export function RolesSection(): React.JSX.Element {
  const { t } = useTranslation();
  const listQuery = useRoleAssignments();
  const items = listQuery.data?.items ?? [];
  const [editing, setEditing] = useState<RoleAssignmentView | null>(null);
  // The row's Edit button that opened the dialog, so focus can be restored to it
  // on close (R10.13's general focus-restore expectation, R18.6) — see the
  // equivalent ref in `MasterDataSection` for why this is needed on a
  // `DialogTrigger`-less controlled `Dialog`.
  const editTriggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Card className="gap-4 p-[14px]">
      <div>
        <p className="text-[13px] font-semibold text-txt">{t('admin.roles.heading')}</p>
        <p className="text-muted-foreground mt-1 text-[11px]">{t('admin.roles.hint')}</p>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10.5px] uppercase">{t('admin.roles.table.colUser')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.roles.table.colRoles')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.roles.table.colApprover')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.roles.table.colPlatformScope')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.roles.table.colChannelScope')}</TableHead>
            <TableHead className="text-right">
              <span className="sr-only">{t('admin.roles.table.colActions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {listQuery.isError ? (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {t('admin.common.loadError')}
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {listQuery.isLoading ? t('admin.common.loading') : t('admin.roles.table.empty')}
              </TableCell>
            </TableRow>
          ) : (
            items.map((assignment) => (
              <TableRow key={assignment.userId}>
                <TableCell className="text-[12.3px] text-txt">{assignment.userDisplayName}</TableCell>
                <TableCell className="text-[12.3px] whitespace-normal">
                  {assignment.roles.length === 0
                    ? t('admin.roles.table.noRoles')
                    : assignment.roles.map((role) => t(`admin.roles.role.${role}`)).join(' · ')}
                </TableCell>
                <TableCell className="text-[12.3px]">
                  {assignment.approverRights ? t('admin.common.yes') : t('admin.common.no')}
                </TableCell>
                <TableCell className="text-[12.3px]">
                  {assignment.platformScope.length === 0
                    ? t('admin.roles.table.scopeNone')
                    : t('admin.roles.table.scopeCount', { count: assignment.platformScope.length })}
                </TableCell>
                <TableCell className="text-[12.3px]">
                  {assignment.channelScope.length === 0
                    ? t('admin.roles.table.scopeNone')
                    : t('admin.roles.table.channelScopeCount', { count: assignment.channelScope.length })}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    onClick={(event) => {
                      editTriggerRef.current = event.currentTarget;
                      setEditing(assignment);
                    }}
                  >
                    {t('admin.common.edit')}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <RoleEditorDialog
        assignment={editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null);
          }
        }}
        onSaved={() => setEditing(null)}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          editTriggerRef.current?.focus();
        }}
      />
    </Card>
  );
}

/**
 * Whether approver rights would have no effect for a user holding exactly `roles`
 * (R9.3, R2.5).
 *
 * A status transition needs two independent things: the approver-rights flag, and a
 * role granting `initiative:write` so the write is permitted at all. No read-only
 * role and not `system_administrator` grant the latter, so the flag alone changes
 * nothing for them.
 *
 * Asked of the domain's own role-to-capability table rather than by naming roles
 * here, so a future role gaining `initiative:write` needs no edit in this file.
 *
 * **Advisory only.** A `true` here does not mean the grant is wrong: `roles` is the
 * *directly assigned* set, and the role that makes the flag usable may arrive
 * through Identity_Service group membership (R1.2), which this screen cannot see.
 * That is why the caller renders a note and still saves, rather than blocking.
 */
export function approverRightsHaveNoEffect(roles: readonly ComPlanRole[]): boolean {
  return !resolveCapabilities(roles).has('initiative:write');
}

/** The whole-assignment editor for one user (R1.9). */
function RoleEditorDialog({
  assignment,
  open,
  onOpenChange,
  onSaved,
  onCloseAutoFocus,
}: {
  readonly assignment: RoleAssignmentView | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSaved: () => void;
  /**
   * Restores keyboard focus to the row's Edit button that opened this dialog
   * (R10.13's general focus-restore expectation, R18.6) — see the equivalent
   * prop on {@link RecordEditorDialog} for why this is needed on a
   * `DialogTrigger`-less controlled `Dialog`.
   */
  readonly onCloseAutoFocus?: (event: Event) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const updateMutation = useUpdateRoleAssignment();
  const platformsQuery = useMasterDataSet('platform', false);
  const channelsQuery = useMasterDataSet('channel', false);

  const [roles, setRoles] = useState<readonly ComPlanRole[]>([]);
  const [approverRights, setApproverRights] = useState(false);
  const [platformScope, setPlatformScope] = useState<readonly string[]>([]);
  const [channelScope, setChannelScope] = useState<readonly string[]>([]);

  // Seed from the assignment when the target user changes.
  const seedKey = `${assignment?.userId ?? ''}:${String(open)}`;
  const [lastSeed, setLastSeed] = useState('');
  if (seedKey !== lastSeed) {
    setLastSeed(seedKey);
    setRoles(assignment?.roles ?? []);
    setApproverRights(assignment?.approverRights ?? false);
    setPlatformScope(assignment?.platformScope ?? []);
    setChannelScope(assignment?.channelScope ?? []);
    updateMutation.reset();
  }

  if (assignment === null) {
    return null;
  }

  const toggle = <T,>(list: readonly T[], value: T, on: boolean): readonly T[] =>
    on ? [...list, value] : list.filter((item) => item !== value);

  const showsPowerlessApproverAdvice = approverRights && approverRightsHaveNoEffect(roles);

  const save = (): void => {
    updateMutation.mutate(
      {
        userId: assignment.userId,
        roles: [...roles],
        approverRights,
        platformScope: [...platformScope],
        channelScope: [...channelScope],
      },
      { onSuccess: onSaved },
    );
  };

  return (
    <EditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.roles.editor.title', { user: assignment.userDisplayName })}
      width="max-w-[560px]"
      {...(onCloseAutoFocus ? { onCloseAutoFocus } : {})}
    >
      <FindingsAlert findings={findingsFromError(updateMutation.error)} />

      <fieldset className="border-0 p-0">
        <legend className="text-[11.5px] font-semibold text-txt">
          {t('admin.roles.editor.rolesLegend')}
        </legend>
        <p className="text-muted-foreground text-[11px]">{t('admin.roles.editor.rolesHint')}</p>
        <div className="mt-1 grid grid-cols-2 gap-1">
          {COMPLAN_ROLES.map((role) => (
            <label key={role} className="flex items-center gap-2 text-[12px] text-txt">
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={(event) => setRoles((current) => toggle(current, role, event.target.checked))}
              />
              <span>{t(`admin.roles.role.${role}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div>
        <label className="flex items-center gap-2 text-[12px] text-txt">
          <input
            type="checkbox"
            checked={approverRights}
            onChange={(event) => setApproverRights(event.target.checked)}
          />
          <span>{t('admin.roles.editor.approverRights')}</span>
        </label>
        <p className="text-muted-foreground mt-1 text-[11px]">
          {t('admin.roles.editor.approverRightsHint')}
        </p>
        {/* Advisory, deliberately not a validation error. The flag is stored
            regardless, because the role that makes it usable may arrive through
            group membership this screen cannot read — so refusing the save would
            block a legitimate grant. It only states what the *visible* role set
            implies. */}
        {showsPowerlessApproverAdvice ? (
          <Alert
            data-testid="roles-approver-no-write-role"
            className="mt-2 border-[var(--warn)] py-2"
          >
            <AlertDescription className="text-[11px]">
              {t('admin.roles.editor.approverRightsNoWriteRole', {
                role: t('admin.roles.role.commercial_planning'),
              })}
            </AlertDescription>
          </Alert>
        ) : null}
      </div>

      <ScopeFieldset
        legend={t('admin.roles.editor.platformScopeLegend')}
        hint={t('admin.roles.editor.scopeHint')}
        options={(platformsQuery.data?.items ?? []).map((r) => ({ id: r.id, name: r.name }))}
        selected={platformScope}
        onToggle={(id, on) => setPlatformScope((current) => toggle(current, id, on))}
      />
      <ScopeFieldset
        legend={t('admin.roles.editor.channelScopeLegend')}
        hint={t('admin.roles.editor.scopeHint')}
        options={(channelsQuery.data?.items ?? []).map((r) => ({ id: r.id, name: r.name }))}
        selected={channelScope}
        onToggle={(id, on) => setChannelScope((current) => toggle(current, id, on))}
      />

      <EditorDialogActions>
        <Button type="button" onClick={save} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? t('admin.common.saving') : t('admin.common.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
          {t('admin.common.cancel')}
        </Button>
      </EditorDialogActions>
    </EditorDialog>
  );
}

/** A checkbox group of Platform or Channel records selected into a read scope (R2.4). */
function ScopeFieldset({
  legend,
  hint,
  options,
  selected,
  onToggle,
}: {
  readonly legend: string;
  readonly hint: string;
  readonly options: readonly { readonly id: string; readonly name: string }[];
  readonly selected: readonly string[];
  readonly onToggle: (id: string, on: boolean) => void;
}): React.JSX.Element {
  const selectedSet = new Set(selected);
  return (
    <fieldset className="border-0 p-0">
      <legend className="text-[11.5px] font-semibold text-txt">{legend}</legend>
      <p className="text-muted-foreground text-[11px]">{hint}</p>
      <div className="mt-1 grid grid-cols-2 gap-1">
        {options.map((option) => (
          <label key={option.id} className="flex items-center gap-2 text-[12px] text-txt">
            <input
              type="checkbox"
              checked={selectedSet.has(option.id)}
              onChange={(event) => onToggle(option.id, event.target.checked)}
            />
            <span>{option.name}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
